/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import {
  Form as AForm,
  Select,
  DatePicker,
  InputNumber,
  Checkbox,
  Radio,
  Input,
  FormItemProps,
  FormInstance,
  FormProps,
  Row,
  Col,
} from 'antd';
import { AIcon as Icon, EyeOutlined, EyeInvisibleOutlined } from '../icon';
import dayjs from 'dayjs';
import { get, set, sortBy, first, isObject } from 'lodash';
import {
  FormEvent,
  MouseEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MultiSelectSearch } from '../multi-select-search';
import {
  SerialCheck,
  SerialNumberInput,
  SerialValidator,
  serialNumberRule,
} from '../serial-number-input';
import { guid } from '../../utils';

interface Item extends FormItemProps {
  canDisplay?: () => boolean;
  title: string | (() => string);
  type?: string;
  var: string;
  props?: {};
  opts?: () => { value: any; label: string }[];
  subtext?: () => string;
  data?: () => any;
  selected?: any;
  vals?: string[];
  subContent?: (item?: any) => any;
  disabled?: boolean | (() => boolean);
  alwaysDisabled?: boolean;
  // type: 'serialNumber' (AER-1795)
  validateSerial?: SerialValidator;
  onSerialValidated?: (serial: string, errors: string[]) => void | Promise<void>;
}

interface IProps extends Omit<FormProps, 'children'> {
  items?: Item[];
  toDisplay?: Item[];
  defaults?: any;
  registerForm?: (form: any) => void;
  children?: any;
  onSubmit?: (e?: MouseEvent | FormEvent) => Promise<void>;
  onValuesChange?: (changed: any, all: any) => void;
  forceUpdate?: any;
}

const styles = {
  row: `
    display: flex;
    flex-direction: row;
    align-items: space-between;
  `,
  marg: `
    margin: 0 8px;
  `
};

// Convert moment objects to dayjs for antd v5 DatePicker compatibility
const convertMomentToDayjs = (values: any): any => {
  if (!values || typeof values !== 'object') return values;
  const result: any = {};
  for (const key of Object.keys(values)) {
    const val = values[key];
    if (val && typeof val === 'object' && typeof val.toISOString === 'function' && typeof val._isAMomentObject !== 'undefined') {
      result[key] = dayjs(val.toISOString());
    } else {
      result[key] = val;
    }
  }
  return result;
};

const AddToForm = (form: any) => {
  return {
    ...form,
    validateFieldsAndScroll: (cb: any) => {
      form
        .validateFields()
        // AER-1656: `validateFields()` (no name list) only resolves values for
        // currently-mounted fields under @rc-component/form. Multi-step wizards
        // (edit-location, edit-org) render one screen at a time via renderScreen(),
        // so step-1 fields (orgId/name/address...) are UNMOUNTED at the final Submit
        // and are omitted from `values` — even though they live in the store via
        // `preserve: true` (validated earlier on "Next"). Consumers then read them
        // as '' and removeEmpty() strips them, saving a blank record. Merge the full
        // store (getFieldsValue(true) — includes preserved/unmounted fields) under
        // the freshly-validated `values` so submit() receives every field while the
        // just-validated values still win. Safe for single-screen forms: when all
        // fields are mounted, getFieldsValue(true) is the same set validateFields()
        // already returns, so the result is unchanged.
        .then((values: any) => {
          cb(null, { ...form.getFieldsValue(true), ...values });
        })
        // AER-1667 (R4): on a failed submit-time validation, move focus to the
        // first errored field so the user lands directly on what needs fixing.
        .catch((err: any) => {
          const errorFields = err && err.errorFields;
          if (errorFields && errorFields.length && typeof form.scrollToField === 'function') {
            try {
              form.scrollToField(errorFields[0].name, { focus: true });
            } catch (e) {
              // field may be unmounted (multi-step wizards); ignore.
            }
          }
          cb(err, {});
        });
    },
  }
}

// Custom item props that should NOT be spread onto AForm.Item.
// AER-1620: `required` is sometimes provided as a function (e.g. `required: () => false`)
// for the consumer's own gating logic (see isSubmitDisabled in add-user). React/AntD treat
// a truthy function value for `required` as "required: true" and render the red asterisk,
// so we strip it here and only forward `required` via resolved `options.rules`.
const CUSTOM_ITEM_KEYS = ['canDisplay', 'title', 'type', 'var', 'props', 'opts', 'subtext', 'data', 'selected', 'vals', 'subContent', 'options', 'style', 'required', 'value', 'transform', 'disabled', 'alwaysDisabled', 'validateSerial', 'onSerialValidated'];

// AER-1741: normalize an item's `opts()` list into AntD's data-driven `options`
// array, cached against the identity of the source array. rc-select memoizes its
// option flattening and its value/label lookup maps on the `options` prop, so a
// consumer that memoizes `opts()` (see the Organization picker in add-user) hands
// the Select the very same array on every render and rc-select skips the work
// entirely. A changed source array — new data — rebuilds as normal. WeakMap so a
// list that is no longer referenced is collected with its cache entry.
const selectOptionsCache = new WeakMap<object, { value: any; label: any }[]>();

const toSelectOptions = (opts: any[]): { value: any; label: any }[] => {
  const cached = selectOptionsCache.get(opts);
  if (cached) {
    return cached;
  }
  const mapped = opts.map((opt: any) => ({ value: opt.value, label: opt.label || opt.title }));
  selectOptionsCache.set(opts, mapped);
  return mapped;
};

const getFormItemProps = (item: any): any => {
  const result: any = {};
  for (const key of Object.keys(item)) {
    if (!CUSTOM_ITEM_KEYS.includes(key)) {
      result[key] = item[key];
    }
  }
  return result;
};

export const Form = forwardRef((props: IProps, ref: React.Ref<any>) => {
  const [vals, setvals] = useState({});
  const [state, setState] = useState({
    error: null,
  });

  // AER-1667 (R3): names of fields that currently show a validation error. Such a
  // field re-validates on every keystroke (onChange) so the user sees it clear in
  // real time; once cleared it reverts to validating on blur only (R2).
  const [erroredFields, setErroredFields] = useState<Set<string>>(() => new Set());

  const refs = useRef(null);

  // AER-1795: per-field record of the serial each `serialNumber` item's rule last checked,
  // shared between that rule and its <SerialNumberInput>.
  const serialChecks = useRef<{ [name: string]: SerialCheck }>({});
  const getSerialCheck = (name: string) => {
    if (!serialChecks.current[name]) {
      serialChecks.current[name] = {};
    }
    return serialChecks.current[name];
  };

  const [_form] = AForm.useForm();

  const form = props.form || _form;

  // Backwards compat: accept toDisplay as alias for items
  const formItems = props.items || (props as any).toDisplay;

  // Backwards compat: accept defaults as alias for initialValues
  const initialValues = convertMomentToDayjs(props.initialValues || (props as any).defaults);

  // Backwards compat: call registerForm with the form instance
  const registerFormRef = useRef(false);
  useEffect(() => {
    if (props.registerForm && !registerFormRef.current) {
      registerFormRef.current = true;
      props.registerForm(AddToForm(form));
    }
  }, [props.registerForm, form]);

  // Backwards compat: sync defaults to form values when they change (matching old Form.create behavior)
  const defaultsRef = useRef(initialValues);
  useEffect(() => {
    const defaults = convertMomentToDayjs(props.initialValues || (props as any).defaults);
    if (defaults && defaults !== defaultsRef.current) {
      defaultsRef.current = defaults;
      const items = formItems || [];
      const flatItems = items.reduce((acc: any[], item: any) => {
        if (Array.isArray(item)) {
          return [...acc, ...item.reduce((a2: any[], i2: any) => Array.isArray(i2) ? [...a2, ...i2] : [...a2, i2], [])];
        }
        return [...acc, item];
      }, []);
      const validKeys = flatItems.filter((td: any) => get(td, 'canDisplay', () => true)()).map((td: any) => td.var);

      const newObj: any = {};
      const loopObj = (obj: any, key = '') => {
        for (const k in obj) {
          const _k = `${key ? key + '.' : ''}${k}`;
          const val = get(obj, k);
          if (val && isObject(val) && !get(val, 'toDate') && !get(val, '_isAMomentObject') && !dayjs.isDayjs(val)) {
            loopObj(val, _k);
          } else {
            if (validKeys.includes(_k)) {
              newObj[_k] = val;
            }
          }
        }
      };
      loopObj(defaults);
      form.setFieldsValue(newObj);
    }
  }, [props.initialValues, (props as any).defaults]);

  const onFinish = (values: any) => {
    props.onFinish && props.onFinish(values);
  };

  const onFinishFailed = (resp: any) => {
    const { errorFields } = resp;
    // AER-1667 (R4): focus the first errored field on a failed native submit.
    if (errorFields && errorFields.length && typeof form.scrollToField === 'function') {
      try {
        form.scrollToField(errorFields[0].name, { focus: true });
      } catch (e) {
        // field may be unmounted; ignore.
      }
    }
    props.onFinishFailed && props.onFinishFailed(resp);
  };

  // AER-1667 (R3): recompute which fields currently show an error after any field
  // meta change (validation runs on blur/change/submit). This drives the per-Item
  // validateTrigger below so an errored field validates live until it is fixed.
  const syncErroredFields = () => {
    const next = new Set<string>();
    const fieldsError = (form.getFieldsError && form.getFieldsError()) || [];
    fieldsError.forEach((f: any) => {
      if (f && f.errors && f.errors.length > 0) {
        next.add(Array.isArray(f.name) ? f.name.join('.') : f.name);
      }
    });
    setErroredFields(prev => {
      if (prev.size === next.size && Array.from(next).every(n => prev.has(n))) {
        return prev;
      }
      return next;
    });
  };

  const onFieldsChange = (changed: any[], all: any[]) => {
    syncErroredFields();
    const consumerHandler = (props as any).onFieldsChange;
    consumerHandler && consumerHandler(changed, all);
  };

  const onValuesChange = (v: any, a: any) => {
    setvals(a);
    props.onValuesChange && props.onValuesChange(a, v);
    // Backwards compat: call onChange with (changedValues, form)
    const legacyOnChange = (props as any).onChange;
    legacyOnChange && legacyOnChange(v, AddToForm(form));
    props.forceUpdate && props.forceUpdate.forceUpdate();
  };

  // AER-1714: notify the form of a value that was written to the store
  // programmatically rather than through a Form.Item-bound control.
  // @rc-component/form only calls `onValuesChange` from its internal
  // `updateValue` (the path a bound control's onChange takes) — a
  // `setFieldValue`/`setFieldsValue` write re-renders the bound Field and
  // nothing above it. `MultiSelectSearch` (`type: 'AutoComplete'`) is the one
  // control here that bypasses the binding and writes the store by hand, so a
  // tag selection used to repaint nothing: any UI derived from that value went
  // stale until an unrelated update (a Pusher tick, a redux change) happened to
  // re-render the owner. On Add Service Record that made the `Serial - X` field
  // take anywhere from ~400 ms to 10 s to appear. The (changed, all) pair below
  // mirrors what rc-form itself passes on `updateValue`.
  const notifyProgrammaticChange = (name: string, value: any) => {
    const changed = { [name]: value };
    onValuesChange(changed, { ...form.getFieldsValue(), ...changed });
  };

  const onSubmit = (item: any) => {
    props.onSubmit && props.onSubmit();
  };

  useImperativeHandle(
    ref,
    () => (AddToForm(form)),
    [vals, form]
  );

  const renderItems = (items: any[] = [], _i = '0') => {
    const isRow = Array.isArray(first(items));
    const hasMarg = _i != '0';
    return (
      <div key={`${_i}`} css={css(`flex: 1;`, hasMarg && `margin: 0 5px;`, hasMarg && styles.marg, isRow && styles.row)}>
        {items.map((item, i) => {
          if (Array.isArray(item)) {
            return renderItems(item, `${_i}_${i}`);
          }

          const canDisplay = item.canDisplay ? item.canDisplay() : true;
          const loading = get(item, 'props.loading');
          // AER-1628: the shared Form previously ignored `item.disabled`/`item.alwaysDisabled`,
          // so View-mode fields (e.g. My Account) rendered editable. Resolve a per-field disabled
          // value and pass it to every control. Defaults to enabled when nothing is provided.
          const isDisabled =
            (item.alwaysDisabled === true) ||
            (typeof item.disabled === 'function' ? item.disabled() : !!item.disabled) ||
            get(item, 'props.disabled', false);
          const inputProps: any = {
            ...(item.props || {}),
          };
          // AntD6: `initialValue` belongs on Form.Item, not the control. A few item defs
          // still carry it inside `props` (e.g. manufacture-process eqType); strip it from
          // the control spread so it doesn't leak onto the DOM input as an unknown attribute
          // (React warning). Field seeding goes through the form's initialValues /
          // setFieldsValue, not the control element.
          delete inputProps.initialValue;
          // onPressEnter is only valid on Input components, not Select/DatePicker/Checkbox
          const textInputProps = {
            ...inputProps,
            onPressEnter: () => {
              onSubmit(item);
            },
          };

          let label = typeof item.title === 'function' ? item.title() : item.title || item.label;
          const formItemProps = getFormItemProps(item);
          // AER-1620: `options` is sometimes provided as a function so the consumer can
          // make `rules` conditional on other field values (see Organization in add-user).
          // Resolve it here so the `rules: [{ required: true }]` actually reaches AForm.Item.
          const rawOptions = (item as any).options;
          const resolvedOptions =
            typeof rawOptions === 'function' ? rawOptions() || {} : (rawOptions || {});

          // AER-1667 (R2/R3): required fields validate on blur by default; once a
          // field is already showing an error it also re-validates on every
          // keystroke (onChange) so the user watches it clear, then reverts to
          // blur-only once cleared. Any consumer-supplied trigger (e.g. an
          // `options.validateTrigger`) is honored as the non-errored base.
          const baseValidateTrigger =
            resolvedOptions.validateTrigger || formItemProps.validateTrigger || 'onBlur';
          const fieldValidateTrigger = erroredFields.has(item.var)
            ? ['onChange', 'onBlur']
            : baseValidateTrigger;

          // AER-1731: a Checkbox carries its state on `checked`, not `value`. Without
          // `valuePropName`, <AForm.Item name=...> injects `value` into the control and
          // @rc-component/form's defaultGetValueFromEvent reads that same stale `value`
          // straight back off the change event (rc-checkbox builds it as
          // `target: { ...props, checked }`) — so the box ticks visually while the store
          // is never updated and the field submits its initial value forever. Default it
          // for every checkbox item rather than leaving each consumer to remember; an
          // explicitly supplied `valuePropName` (edit-org, edit-equipment, registration)
          // still wins. Only `checkbox` needs this: Checkbox.Group / Radio.Group bind
          // `value` as normal.
          const checkboxValueProps =
            item.type === 'checkbox' && !resolvedOptions.valuePropName && !formItemProps.valuePropName
              ? { valuePropName: 'checked' }
              : {};

          // AER-1795: a serial-number item validates through a Form.Item rule appended to
          // its own rules, so the check also runs on submit.
          const serialCheck = item.type === 'serialNumber' ? getSerialCheck(item.var) : undefined;
          const serialRuleProps =
            serialCheck && item.validateSerial
              ? { rules: [...(resolvedOptions.rules || []), serialNumberRule(item.validateSerial, serialCheck)] }
              : {};

          return !canDisplay ? null : (
            <div key={`${_i}__${i}`} css={css(`position: relative;`, item.style)}>
              <AForm.Item
                {...formItemProps}
                name={item.var}
                label={
                  item.type === 'checkbox' || item.type === 'radio' || item.type === 'radiogroup'
                    ? ''
                    : label
                }
                {...resolvedOptions}
                {...checkboxValueProps}
                {...serialRuleProps}
                validateTrigger={fieldValidateTrigger}
              >
                {item.type === 'dropdown' && item.opts ? (
                  <Select placeholder={(item as any).placeholder} {...inputProps} disabled={isDisabled}>
                    {(typeof item.opts === 'function' ? item.opts() : item.opts).map((opt: any, i: number) => (
                      <Select.Option key={i} value={opt.value}>
                        {opt.label || opt.title}
                      </Select.Option>
                    ))}
                  </Select>
                ) : item.type === 'SelectLimited' && item.opts ? (
                  // AER-1620: Organization picker in Invite User. Renders the orgs list
                  // (item.opts()) as a searchable Select dropdown. We use AntD Select
                  // directly rather than the SelectLimited class so AForm.Item can wire
                  // value/onChange through `name`.
                  // AER-1741: feed AntD the data-driven `options` array instead of
                  // <Select.Option> children. As children, React had to construct one
                  // element per organization (4,205 on QA) on every render — and Invite
                  // User re-renders the whole form on each keystroke (`forceUpdate={this}`)
                  // — while rc-select re-walked all of them to rebuild its value/label
                  // lookup, because a fresh children array is never memo-stable. That was
                  // ~150 ms per typed character. With a stable `options` array rc-select
                  // memoizes the whole chain and does no work at all unless the list or
                  // the search text actually changes. `optionFilterProp` moves to "label"
                  // since there are no children left to match against; the list is not
                  // truncated, so every org still shows and searches.
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={(item as any).placeholder}
                    options={toSelectOptions(typeof item.opts === 'function' ? item.opts() : item.opts)}
                    {...inputProps}
                    disabled={isDisabled}
                  />
                ) : item.type === 'textarea' ? (
                  <Input.TextArea rows={3} {...textInputProps} disabled={isDisabled} />
                ) : (item.type === 'datepicker' || item.type === 'date') ? (
                  <DatePicker style={{ width: '100%' }} {...inputProps} disabled={isDisabled} />
                ) : item.type === 'number' ? (
                  <InputNumber {...textInputProps} disabled={isDisabled} />
                ) : item.type === 'checkbox' ? (
                  // AER-1730: in JSX an explicit child always beats a `children` arriving
                  // through the `{...inputProps}` spread, so an item that supplies a rich
                  // label (Equipment Registration's consent row, with its Privacy Policy /
                  // Terms of Use links) had it silently collapsed to the plain `item.title`.
                  // Honor the item's own children when it provides them; every other
                  // checkbox supplies none and keeps rendering its title unchanged.
                  <Checkbox {...inputProps} disabled={isDisabled}>
                    {get(item, 'props.children', label)}
                  </Checkbox>
                ) : item.type === 'checkboxgroup' ? (
                  <Checkbox.Group {...inputProps} disabled={isDisabled}>
                    <Row>
                      <div>
                        <label>{item.title}</label>
                      </div>
                      {(get(item, 'vals', []) as any[]).map((v: any, _i: number) => {
                        const showRedBorder = false;
                        return (
                          <Col key={_i}>
                            <Checkbox
                              id={showRedBorder ? 'nophone' : undefined}
                              disabled={isDisabled}
                              key={_i}
                              value={v}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </Checkbox.Group>
                ) : (item.type === 'radio' || item.type === 'radiogroup') ? (
                  // AER-1631: 2FA settings renders the `mfa` field (vals ['email','sms'])
                  // as a radio group. Mirrors the checkboxgroup layout so the Radio buttons
                  // line up under the Notification Type / Email / SMS column headers.
                  <Radio.Group {...inputProps} disabled={isDisabled} style={{ width: '100%' }}>
                    <Row>
                      <div style={{ width: get(item, 'labelWidth', 150), float: 'left' }}>
                        <label style={{ fontSize: 12 }}>{item.title}</label>
                      </div>
                      {(get(item, 'vals', []) as any[]).map((v: any, _i: number) => (
                        <Col
                          key={_i}
                          style={{ width: get(item, 'colWidth', 50), textAlign: 'center', float: 'left' }}
                        >
                          <Radio key={_i} value={v} />
                        </Col>
                      ))}
                    </Row>
                  </Radio.Group>
                ) : item.type === 'AutoComplete' ? (
                  <MultiSelectSearch
                    dataSource={item.data && sortBy(item.data())}
                    placeholder={'Search...'}
                    onTagsChange={selected => {
                      item.selected = selected;
                      form.setFieldValue(item.var, selected);
                      // AER-1714: `setFieldValue` re-renders the bound Field and
                      // nothing else — notify the form so the Form (and any owner
                      // wired through `forceUpdate`) repaints on this selection.
                      notifyProgrammaticChange(item.var, selected);
                    }}
                    selected={form.getFieldValue(item.var)}
                    {...inputProps}
                    disabled={isDisabled}
                  />
                ) : item.type === 'serialNumber' && serialCheck ? (
                  <SerialNumberInput
                    {...textInputProps}
                    name={item.var}
                    check={serialCheck}
                    onValidated={item.onSerialValidated}
                    disabled={isDisabled}
                  />
                ) : item.type === 'password' ? (
                  <Input.Password
                    {...textInputProps}
                    disabled={isDisabled}
                    iconRender={(visible: boolean) => (visible ? <EyeInvisibleOutlined /> : <EyeOutlined />)}
                  />
                ) : (
                  <Input
                    ref={comp => set(refs, item.var, comp)}
                    {...textInputProps}
                    disabled={isDisabled}
                  />
                )}
              </AForm.Item>

              {item.subContent && item.subContent(item)}
              {loading && <Icon type="loading" />}
              {item.subtext && <div>{item.subtext()}</div>}
            </div>
          );
        })}
      </div>
    )
  }

  // Extract only valid AForm props, excluding our custom ones
  const { layout, name, colon, disabled, component, preserve, requiredMark, scrollToFirstError, validateMessages, validateTrigger, labelAlign, labelCol, labelWrap, wrapperCol, size, variant, className, style } = props as any;
  // Emotion compiles a `css` prop into both a className and the runtime style
  // tag, but our Form forwardRef doesn't pull `css` off of `props` (Emotion
  // strips it before render in the JSX pragma transform). What does land on
  // `props` is the compiled className that emotion injects.
  const aFormProps: any = {};
  if (layout !== undefined) aFormProps.layout = layout;
  if (name !== undefined) aFormProps.name = name;
  if (colon !== undefined) aFormProps.colon = colon;
  if (disabled !== undefined) aFormProps.disabled = disabled;
  if (component !== undefined) aFormProps.component = component;
  if (preserve !== undefined) aFormProps.preserve = preserve;
  if (requiredMark !== undefined) aFormProps.requiredMark = requiredMark;
  // AER-1667 (R4): default to scrolling AND focusing the first error on a native
  // submit (consumer may override with their own scrollToFirstError).
  aFormProps.scrollToFirstError = scrollToFirstError !== undefined ? scrollToFirstError : { focus: true };
  if (validateMessages !== undefined) aFormProps.validateMessages = validateMessages;
  // AER-1667 (R2): default required-field validation to fire on blur. This is the
  // form-level fallback for any raw <Form.Item> children; items rendered from
  // `items=` get an explicit per-Item trigger above (which still wins).
  aFormProps.validateTrigger = validateTrigger !== undefined ? validateTrigger : 'onBlur';
  if (labelAlign !== undefined) aFormProps.labelAlign = labelAlign;
  if (labelCol !== undefined) aFormProps.labelCol = labelCol;
  if (labelWrap !== undefined) aFormProps.labelWrap = labelWrap;
  if (wrapperCol !== undefined) aFormProps.wrapperCol = wrapperCol;
  if (size !== undefined) aFormProps.size = size;
  if (variant !== undefined) aFormProps.variant = variant;
  // AER-1465: forward className + style so callers using `css={css('...')}`
  // (which Emotion compiles to className) actually width-clamp the form.
  if (className !== undefined) aFormProps.className = className;
  if (style !== undefined) aFormProps.style = style;

  return (
    <AForm
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      {...aFormProps}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
      onFieldsChange={onFieldsChange}
    >
      {renderItems(formItems)}

      {props.children}
    </AForm>
  );
});

(Form as any).Item = AForm.Item;

export const WithForm = (Comp: any) => {
  return ({ wrappedComponentRef, ...props }: any) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [form] = AForm.useForm();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formKey = useRef(guid());

    // AER-1620: keep `renderForm` and `formProps` referentially stable across renders.
    // Without memoization, every Redux/Pusher tick produced a brand-new `renderForm`
    // closure, which React treated as a new component type and unmounted/remounted the
    // entire form on each render — so any input being typed into lost focus mid-keystroke.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const renderForm = useCallback(
      (p: any) => <Form key={formKey.current} {...p} form={form} />,
      [form]
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const formProps = useMemo(
      () => ({
        ...AddToForm(form),
        Form: renderForm,
      }),
      [form, renderForm]
    );

    // AER-1628: AntD3 `Form.create()` (used on master/prod) honored
    // `wrappedComponentRef` by forwarding a ref to the wrapped class instance.
    // The new HOC must do the same so callers (e.g. ModalVerify) can grab the
    // instance and call imperative methods like `openModal`. When the prop is
    // absent, `ref` is undefined → no-op (safe for all other consumers).
    return <Comp key={formKey.current} ref={wrappedComponentRef} {...props} form={formProps} />
  }
}
